import { useMemo, useState } from "react";
import { Sparkles } from "lucide-react";

// Left/Right site pairs — mirrored on the pill columns flanking the body image.
// Ordered top-to-bottom the same way anatomy is stacked on the body.
const FRONT_PAIRS = [
  { key: "shoulder",   label: "Shoulder" },
  { key: "chest",      label: "Chest" },
  { key: "upper-arm",  label: "Upper Arm" },
  { key: "love-handle",label: "Love Handle" },
  { key: "upper-abs",  label: "Upper Abs" },
  { key: "mid-abs",    label: "Mid Abs" },
  { key: "lower-abs",  label: "Lower Abs" },
  { key: "thigh",      label: "Thigh" },
  { key: "inner-thigh",label: "Inner Thigh" },
];

const BACK_PAIRS = [
  { key: "shoulder",   label: "Shoulder" },
  { key: "upper-back", label: "Upper Back" },
  { key: "triceps",    label: "Triceps" },
  { key: "lat",        label: "Lat" },
  { key: "lower-back", label: "Lower Back" },
  { key: "upper-glute",label: "Upper Glute" },
  { key: "lower-glute",label: "Lower Glute" },
  { key: "hamstring",  label: "Hamstring" },
  { key: "calf",       label: "Calf" },
];

// Anatomical marker coordinates as % of image (left, top). LEFT = viewer's left.
const FRONT_COORDS = {
  "shoulder":    { lx: 34, ly: 21, rx: 66, ry: 21 },
  "chest":       { lx: 42, ly: 28, rx: 58, ry: 28 },
  "upper-arm":   { lx: 27, ly: 33, rx: 73, ry: 33 },
  "love-handle": { lx: 37, ly: 44, rx: 63, ry: 44 },
  "upper-abs":   { lx: 46, ly: 35, rx: 54, ry: 35 },
  "mid-abs":     { lx: 46, ly: 41, rx: 54, ry: 41 },
  "lower-abs":   { lx: 46, ly: 48, rx: 54, ry: 48 },
  "thigh":       { lx: 42, ly: 69, rx: 58, ry: 69 },
  "inner-thigh": { lx: 47, ly: 72, rx: 53, ry: 72 },
};
const BACK_COORDS = {
  "shoulder":    { lx: 34, ly: 22, rx: 66, ry: 22 },
  "upper-back":  { lx: 43, ly: 28, rx: 57, ry: 28 },
  "triceps":     { lx: 26, ly: 34, rx: 74, ry: 34 },
  "lat":         { lx: 37, ly: 38, rx: 63, ry: 38 },
  "lower-back":  { lx: 46, ly: 47, rx: 54, ry: 47 },
  "upper-glute": { lx: 43, ly: 55, rx: 57, ry: 55 },
  "lower-glute": { lx: 44, ly: 61, rx: 56, ry: 61 },
  "hamstring":   { lx: 43, ly: 74, rx: 57, ry: 74 },
  "calf":        { lx: 44, ly: 86, rx: 56, ry: 86 },
};

// Rotation status palette — simple green (rested / recommended) vs red (recent).
// The single top recommendation gets an extra glow ring + "REC" chip.
const STATUS = {
  top:    { color: "#22C55E", bg: "rgba(34,197,94,0.22)", border: "#22C55E",              ring: "0 0 0 2px rgba(34,197,94,0.45)" },
  rested: { color: "#22C55E", bg: "rgba(34,197,94,0.10)", border: "rgba(34,197,94,0.45)", ring: null },
  recent: { color: "#EF4444", bg: "rgba(239,68,68,0.14)", border: "rgba(239,68,68,0.55)", ring: null },
};

function allSiteLabels() {
  const front = FRONT_PAIRS.flatMap((p) => [`L ${p.label}`, `R ${p.label}`]);
  const back = BACK_PAIRS.flatMap((p) => [`L ${p.label}`, `R ${p.label}`]);
  return Array.from(new Set([...front, ...back]));
}

function SitePill({ site, count, status, windowN, selected, onClick, align }) {
  const style = STATUS[status];
  const isTop = status === "top";
  return (
    <button
      type="button"
      onClick={() => onClick(site.label)}
      className={`w-full text-${align} px-3 py-2 rounded-full border transition-all duration-150 text-sm ${
        selected
          ? "border-[#0D9488] bg-[#0D9488]/15 text-[#0D9488] font-semibold shadow-[0_0_0_1px_rgba(240,185,11,0.4)]"
          : "hover:border-zinc-500"
      }`}
      style={selected ? undefined : {
        backgroundColor: style.bg,
        borderColor: style.border,
        color: style.color,
        boxShadow: style.ring || undefined,
      }}
      data-testid={`site-btn-${site.id}`}
      data-rotation-status={status}
      title={
        isTop
          ? "Recommended — this site has rested the longest"
          : status === "recent"
            ? `Used ${count} time${count === 1 ? "" : "s"} in your last ${windowN} shots`
            : "Rested — good rotation choice"
      }
    >
      <span className={`inline-flex items-center gap-2 w-full ${align === "right" ? "flex-row-reverse" : ""} justify-between`}>
        <span className={`truncate inline-flex items-center gap-1.5 ${align === "right" ? "flex-row-reverse" : ""}`}>
          {isTop && !selected && (
            <span
              className="text-[8px] font-mono-num tabular-nums uppercase tracking-widest px-1 py-0.5 border border-current leading-none"
              data-testid={`site-rec-chip-${site.id}`}
            >
              REC
            </span>
          )}
          <span className="truncate">{site.label}</span>
        </span>
        {count > 0 && (
          <span className="font-mono-num text-[10px] tabular-nums opacity-80 flex-shrink-0">×{count}</span>
        )}
      </span>
    </button>
  );
}

export default function BodyDiagram({ value, onChange, shots = [], rotationWindowN = 10 }) {
  const [view, setView] = useState("front");
  const pairs = view === "back" ? BACK_PAIRS : FRONT_PAIRS;

  // Compute rotation state from the user's last N shots.
  // - recentCountByLabel: how many of the last N shots landed on each labelled site
  // - lastUsedByLabel: timestamp of the latest use per site (across ALL history)
  // - recommendedTop: single "rested" site to nudge the user toward
  const { recentCountByLabel, lastUsedByLabel, recommendedTop, hasHistory } = useMemo(() => {
    const sorted = [...(shots || [])]
      .filter((s) => s?.injection_site && s?.taken_at)
      .sort((a, b) => new Date(b.taken_at) - new Date(a.taken_at));
    const lastN = sorted.slice(0, rotationWindowN);

    const rc = {};
    for (const s of lastN) rc[s.injection_site] = (rc[s.injection_site] || 0) + 1;

    const lu = new Map();
    for (const s of sorted) {
      if (!lu.has(s.injection_site)) {
        lu.set(s.injection_site, new Date(s.taken_at).getTime());
      }
    }

    // Pick top recommendation: prefer never-used predefined sites; otherwise the
    // least-recently-used site that isn't in the recent window.
    const all = allSiteLabels();
    let top = null;
    if (sorted.length > 0) {
      const never = all.filter((l) => !lu.has(l));
      if (never.length) {
        top = never[0];
      } else {
        const notRecent = all.filter((l) => !rc[l]);
        if (notRecent.length) {
          notRecent.sort((a, b) => (lu.get(a) || 0) - (lu.get(b) || 0));
          top = notRecent[0];
        }
      }
    }

    return {
      recentCountByLabel: rc,
      lastUsedByLabel: lu,
      recommendedTop: top,
      hasHistory: sorted.length > 0,
    };
  }, [shots, rotationWindowN]);

  const statusOf = (label) => {
    if (label === recommendedTop) return "top";
    if ((recentCountByLabel[label] || 0) > 0) return "recent";
    return "rested";
  };

  const suggestRested = () => {
    if (recommendedTop) onChange(recommendedTop);
  };

  return (
    <div className="bg-slate-50 border border-slate-200 p-3 sm:p-5" data-testid="body-diagram">
      {/* View toggle — Front / Back / None */}
      <div className="flex gap-2 mb-3" data-testid="body-view-toggle">
        {[
          { k: "front", label: "Front" },
          { k: "back",  label: "Back" },
          { k: "none",  label: "None" },
        ].map((v) => (
          <button
            key={v.k}
            type="button"
            onClick={() => setView(v.k)}
            className={`flex-1 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
              view === v.k
                ? "border-[#0D9488] bg-[#0D9488]/15 text-[#0D9488]"
                : "border-slate-300 text-slate-800 bg-slate-50/40 hover:border-zinc-500"
            }`}
            data-testid={`body-view-${v.k}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Rotation suggestion strip — hidden if user has no logged sites yet */}
      {hasHistory && recommendedTop && view !== "none" && (
        <div
          className="mb-4 flex items-center justify-between gap-3 px-3 py-2 border border-[#22C55E]/40 bg-[#22C55E]/[0.06]"
          data-testid="body-rotation-suggestion"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-3.5 h-3.5 text-[#22C55E] shrink-0" />
            <div className="min-w-0">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#22C55E]/80 leading-tight">
                // Rotation tip
              </div>
              <div className="text-xs text-slate-900 truncate" data-testid="body-rotation-suggestion-label">
                Try <span className="text-[#22C55E] font-semibold">{recommendedTop}</span> — rested the longest
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={suggestRested}
            className="shrink-0 px-3 py-1.5 border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10 font-mono-num text-[10px] uppercase tracking-widest transition-colors"
            data-testid="body-rotation-suggest-btn"
          >
            Use it
          </button>
        </div>
      )}

      {view !== "none" ? (
        <div className="grid grid-cols-[minmax(96px,1fr)_minmax(180px,320px)_minmax(96px,1fr)] gap-2 sm:gap-4 items-start">
          {/* Left column — L side pills */}
          <div className="flex flex-col gap-2" data-testid="body-pills-left">
            {pairs.map((p) => {
              const site = { id: `${view}-l-${p.key}`, side: "L", label: `L ${p.label}`, pairKey: p.key };
              return (
                <SitePill
                  key={site.id}
                  site={site}
                  count={recentCountByLabel[site.label] || 0}
                  status={statusOf(site.label)}
                  windowN={rotationWindowN}
                  selected={value === site.label}
                  onClick={onChange}
                  align="left"
                />
              );
            })}
          </div>

          {/* Center — body image + coloured markers */}
          <div className="flex justify-center">
            <div
              className="rounded-3xl border border-[#0D9488]/50 bg-black overflow-hidden flex items-center justify-center w-full max-w-[320px] relative"
              data-testid="body-image-frame"
            >
              <img
                src={view === "front" ? "/body-front.png" : "/body-back.png"}
                alt={`${view} body diagram`}
                className="w-full h-auto block"
                draggable={false}
              />
              <div className="absolute inset-0">
                {pairs.map((p) => {
                  const coords = (view === "front" ? FRONT_COORDS : BACK_COORDS)[p.key];
                  if (!coords) return null;
                  return ["l", "r"].map((side) => {
                    const label = `${side.toUpperCase()} ${p.label}`;
                    const cx = side === "l" ? coords.lx : coords.rx;
                    const cy = side === "l" ? coords.ly : coords.ry;
                    const count = recentCountByLabel[label] || 0;
                    const selected = value === label;
                    const status = statusOf(label);
                    const style = STATUS[status];
                    const isTop = status === "top";
                    return (
                      <button
                        type="button"
                        key={`${p.key}-${side}`}
                        onClick={() => onChange(label)}
                        className="absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none"
                        style={{ left: `${cx}%`, top: `${cy}%` }}
                        aria-label={label}
                        data-testid={`heat-dot-${view}-${side}-${p.key}`}
                        data-rotation-status={status}
                        title={
                          isTop
                            ? `${label} — recommended (rested the longest)`
                            : status === "recent"
                              ? `${label} — used ${count}× in last ${rotationWindowN} shots`
                              : `${label} — rested`
                        }
                      >
                        {selected && (
                          <span
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#0D9488] animate-pulse pointer-events-none"
                            style={{ width: 26, height: 26 }}
                          />
                        )}
                        {isTop && !selected && (
                          <span
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#22C55E]/70 animate-ping pointer-events-none"
                            style={{ width: 24, height: 24 }}
                          />
                        )}
                        <span
                          className="block rounded-full border-2 shadow-lg transition-transform group-hover:scale-125"
                          style={{
                            width: selected ? 18 : isTop ? 18 : count ? 16 : 12,
                            height: selected ? 18 : isTop ? 18 : count ? 16 : 12,
                            background: selected ? "#0D9488" : style.color,
                            borderColor: selected ? "#0D9488" : "rgba(0,0,0,0.7)",
                            boxShadow: `0 0 8px ${selected ? "#0D9488" : style.color}`,
                            opacity: !count && !isTop && !selected ? 0.75 : 1,
                          }}
                        />
                        {count > 0 && (
                          <span
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[9px] font-bold text-black font-mono-num pointer-events-none"
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  });
                })}
              </div>
            </div>
          </div>

          {/* Right column — R side pills */}
          <div className="flex flex-col gap-2" data-testid="body-pills-right">
            {pairs.map((p) => {
              const site = { id: `${view}-r-${p.key}`, side: "R", label: `R ${p.label}`, pairKey: p.key };
              return (
                <SitePill
                  key={site.id}
                  site={site}
                  count={recentCountByLabel[site.label] || 0}
                  status={statusOf(site.label)}
                  windowN={rotationWindowN}
                  selected={value === site.label}
                  onClick={onChange}
                  align="right"
                />
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-slate-500 text-sm font-mono-num text-center py-6 border border-dashed border-slate-200 rounded-lg">
          Type a custom site below.
        </div>
      )}

      {/* Legend — matches the rotation palette */}
      {view !== "none" && (
        <div className="mt-4 flex items-center justify-center gap-4 flex-wrap text-[11px] font-medium" data-testid="body-rotation-legend">
          <span className="inline-flex items-center gap-1.5" data-testid="legend-top">
            <span
              className="inline-block w-3 h-3 rounded-sm border"
              style={{ background: STATUS.top.bg, borderColor: STATUS.top.border, boxShadow: STATUS.top.ring }}
            />
            <span style={{ color: STATUS.top.color }}>Recommended</span>
          </span>
          <span className="inline-flex items-center gap-1.5" data-testid="legend-rested">
            <span
              className="inline-block w-3 h-3 rounded-sm border"
              style={{ background: STATUS.rested.bg, borderColor: STATUS.rested.border }}
            />
            <span style={{ color: STATUS.rested.color }}>Rested</span>
          </span>
          <span className="inline-flex items-center gap-1.5" data-testid="legend-recent">
            <span
              className="inline-block w-3 h-3 rounded-sm border"
              style={{ background: STATUS.recent.bg, borderColor: STATUS.recent.border }}
            />
            <span style={{ color: STATUS.recent.color }}>Recent</span>
          </span>
          <span className="text-slate-400" data-testid="body-rotation-window-label">// last {rotationWindowN} shots</span>
        </div>
      )}
    </div>
  );
}
